import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useEffect, useState } from 'react';
import SEO from '@/components/SEO';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground leading-relaxed transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const MaximCard = ({ number, title, description, delay = 0 }: { 
  number: number; 
  title: string; 
  description: string; 
  delay?: number; 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center font-bold text-lg">
          {number}
        </div>
        <div className="flex-1">
          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
            {title}
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const MaximsOfEquityPage = () => {
  const maxims = [
    {
      title: "Equity will not allow a wrong to be without a remedy",
      description: "Equity will not allow a person to be denied a remedy for a wrong that has been committed, even if legal statutes fall short or don't provide for it."
    },
    {
      title: "Equity acts in personam",
      description: "Equitable remedies target the person rather than the property involved, focusing on directing the actions or conduct of individuals to achieve justice."
    },
    {
      title: "One who seeks equity must do equity and come with clean hands",
      description: "To ask for fairness, one must act (have acted) fairly, honestly, and ethically; a claimant seeking relief must have behaved justly in relation to the matter at hand; a person guilty of wrongdoing cannot benefit from equity; One who comes to seek equity must come with clean hands."
    },
    {
      title: "Equity looks to intent, conduct, and substance, rather than mere form or technicality",
      description: "Equity prioritizes the intention and substance behind an agreement or act, rather than rigid adherence to its formal structures."
    },
    {
      title: "Equity remedies errors",
      description: "Equity seeks to correct mistakes and imperfections, where law alone would fail to deliver justice."
    },
    {
      title: "Equity regards as done that which ought to be done, and as undone what ought not to be done",
      description: "Equity treats obligations as fulfilled if they are due, even if not physically completed, especially in cases like specific performance; Similarly, equity may treat an action that should not have been taken as if it never occurred, ensuring fairness and preventing unjust outcomes from faulty or fraudulent actions."
    },
    {
      title: "Equity will not assist a volunteer",
      description: "Those who give something without expecting anything in return cannot seek help from equity, as they have no legal obligation or right to claim."
    },
    {
      title: "Notice is equivalent to knowledge",
      description: "As equity operates under the law of notice, when a person has received notice — whether direct or constructive — they are deemed to have knowledge of the matter and cannot claim ignorance."
    },
    {
      title: "Equity aids the vigilant, not those who slumber on their rights",
      description: "Equity supports those who are proactive in asserting their rights in a timely manner, not those who delay or neglect their claims for equitable relief."
    },
    {
      title: "Equity imputes an intention to fulfill an obligation",
      description: "If someone has the ability and opportunity to fulfill a legal obligation, equity assumes they intend to do so."
    },
    {
      title: "Equity delights in equality",
      description: "Equity strives for fairness, often distributing benefits or obligations equally among parties in cases where such fairness is required."
    },
    {
      title: "Equity abhors unjust enrichment",
      description: "Equity intervenes to restore fairness and balance in preventing one party from gaining an unfair benefit by the expense of, detriment to, or harm upon another."
    },
    {
      title: "Equity abhors a forfeiture",
      description: "Equity disfavors the idea of penalizing someone by depriving them of their rights or property; it seeks to prevent unnecessary or unjust forfeiture."
    },
    {
      title: "Equity delights to do justice, and not by halves",
      description: "Equity resolves and ensures all aspects of justice and truth are addressed in one proceeding, rendering a full and complete collection of remedies fitting to the matter(s) at hand."
    },
    {
      title: "Where the equities are equal, the first in time shall prevail",
      description: "If both parties have equally compelling claims in equity, the court will defer to the rights established first."
    },
    {
      title: "The law is nothing without Equity, Equity is everything even without the law",
      description: "Underscores that while laws create boundaries and order, equity represents a higher principle that prioritizes fairness and justice, which can sometimes exist independently of strict legal codes."
    },
    {
      title: "In fictions of law, Equity is always present",
      description: "Equity remains present within legal fictions — such as corporate entities or statutory frameworks — to prevent injustice and ensure fairness, even when strict legal rules might lead to unjust outcomes."
    },
    {
      title: "Equity will not perfect an imperfect gift",
      description: "If a gift has not been legally completed or transferred, equity will not intervene to enforce or perfect it."
    },
    {
      title: "Equity regards the beneficiary as the true owner",
      description: "In trusts, equity sees the beneficiary as the rightful owner of the trust property, even though legal title rests with the trustee."
    },
    {
      title: "Equity will not allow a statute to be used as an instrument of fraud",
      description: "Equity prevents the misuse of statutes to perpetuate fraud or unjust enrichment."
    },
    {
      title: "Equity will not require an idle gesture",
      description: "Equity does not require actions that serve no real purpose, so if an act achieves no substantive result, equity will not order it."
    },
    {
      title: "Equity will not allow a trust to fail for want of a trustee",
      description: "A trust will not fail simply because there is no trustee available."
    },
    {
      title: "Where the equities are equal, the law prevails",
      description: "If both parties have equally compelling claims in equity, the court will defer to established legal rights rather than create a new equitable remedy."
    },
    {
      title: "The law delights in equity",
      description: "At its best, the law recognizes the inherent value of equity, understanding that true justice requires its balance and conscience."
    },
    {
      title: "Equity will not suffer a wrongdoer to benefit from their own wrongdoing",
      description: "Equity ensures that those who engage in wrongful conduct cannot gain an advantage from their misdeeds; A person who seeks to profit from their own injustice will be denied equitable relief, as fairness cannot allow injustice to be rewarded."
    },
    {
      title: "Equity follows the law but does not slavishly adhere to it",
      description: "Equity operates in harmony with the law but allows flexibility in its application; While legal rules are respected, equity focuses on achieving justice and fairness, even when the strict application of the law would lead to an unjust result."
    },
    {
      title: "Equity will not allow hardship to be inflicted by rigid adherence to law",
      description: "Equity prevents unnecessary hardship that could result from an inflexible application of legal statutes; It provides relief when the law's application would lead to injustice or excessive hardship, ensuring that fairness prevails."
    },
    {
      title: "Equity will not allow a party to take advantage of their own delay",
      description: "Equity favors those who act promptly and diligently to assert their rights; Delays without valid reasons may forfeit one's ability to claim equitable relief."
    },
    {
      title: "Equity will not allow a party to take an unfair advantage of another",
      description: "Equity ensures that no party benefits from deceptive tactics or exploitation, especially when one party is in a vulnerable position; Equity seeks to prevent unjust enrichment or undue advantage gained by one party over another."
    },
    {
      title: "Equity will provide a remedy when there is no adequate remedy at law",
      description: "When legal remedies are insufficient or unavailable to provide justice, equity steps in to offer relief; This principle emphasizes equity's role in addressing gaps left by the law, ensuring fairness is maintained even when legal solutions fall short."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Maxims of Equity | Freeman & Crown Law"
        description="Explore the fundamental maxims of equity that govern fairness, justice, and conscience in legal proceedings. Essential principles for equitable relief and natural justice."
        image="/lovable-uploads/d54b2ac6-69a1-4d86-ac6e-e08d24d13ec9.png"
        url={window.location.origin + '/maxims-of-equity'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-8">
              Maxims of Equity
            </h1>
            <div className="max-w-3xl mx-auto lg:max-w-none lg:mx-8 space-y-6">
              <AnimatedParagraph delay={200}>
                Equity operates through moral principles and divine guiding that ensure justice, fairness, and honor beyond the rigid applications of statutory law.
              </AnimatedParagraph>
              <AnimatedParagraph delay={400}>
                Maxims of Equity form the foundation of the Freeman & Crown Equity Law Group, reflecting the deeper truths that govern how fairness is upheld in societies among people. Rooted in conscience, good faith, honor, and justice, these principles remind us that Equity is ever-present, favoring the vigilant, correcting imbalances, and ensuring that those who seek fairness must themselves act with integrity.
              </AnimatedParagraph>
              <AnimatedParagraph delay={600}>
                To know Equity is to recognize its presence beneath the surface of all legal fictions, a force that restores balance where the law alone may falter or be insufficient. While the following is a collection of ones that feel fundamental, many others exist as guidance in the field.
              </AnimatedParagraph>
            </div>
          </div>

          <div className="space-y-8">
            <h2 className="font-rockwell text-3xl font-semibold text-primary text-center mb-12">
              The Maxims of Equity
            </h2>
            
            <div className="grid gap-8">
              {maxims.map((maxim, index) => (
                <MaximCard
                  key={index}
                  number={index + 1}
                  title={maxim.title}
                  description={maxim.description}
                  delay={600 + (index * 100)}
                />
              ))}
            </div>
          </div>

          <div className="mt-16 p-8 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20">
            <h3 className="font-rockwell text-2xl font-semibold text-primary mb-6 text-center">
              The Conscience of the Law
            </h3>
            <div className="max-w-3xl mx-auto space-y-4">
              <AnimatedParagraph delay={1800}>
                Equity serves as the conscience of the legal system, intervening where rigid application of law would 
                produce unconscionable results. These maxims ensure that justice is not merely mechanical but guided by 
                principles of fairness and moral reasoning.
              </AnimatedParagraph>
              <AnimatedParagraph delay={2000}>
                At Freeman & Crown Equity Law Group, we apply these timeless principles to modern challenges, 
                ensuring that every remedy we pursue is grounded in both legal precedent and equitable conscience.
              </AnimatedParagraph>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default MaximsOfEquityPage;